/**
 * Continuous MFN enforcement — trigger diffing (pure logic).
 *
 * When the engine re-evaluates a fund (build doc — 2026-07-01 continuous-
 * enforcement decision), the freshly computed (clause, provision) pairs are
 * compared against the triggers already on file. A live trigger whose pair no
 * longer computes is flagged STALE — never auto-dismissed (that would make the
 * AI authoritative, which the LOCKED rules forbid; a human decides via the
 * existing dismiss/resolve flow). A stale trigger whose pair computes again
 * (the underlying facts reverted) is revived.
 *
 * Resolved and dismissed triggers are permanent human decisions and are never
 * touched by this diff, regardless of whether their pair still computes.
 */

const LIVE_STATUSES = new Set(["open", "reviewing", "extending", "negotiating"]);
const RESOLVED_STATUSES = new Set([
  "resolved_extended",
  "resolved_excluded",
  "resolved_negotiated",
  "dismissed",
]);

export interface ComputedTriggerPair {
  mfnClauseId: string;
  triggeringProvisionId: string;
}

export interface ExistingTriggerRow {
  id: string;
  mfn_clause_id: string;
  triggering_provision_id: string;
  status: string;
  stale: boolean;
}

export interface MfnDiffResult {
  /** Live, non-stale triggers whose (clause, provision) pair is no longer computed. */
  newlyStaleIds: string[];
  /** Stale triggers whose (clause, provision) pair is computed again. */
  revivedIds: string[];
}

/** A collision-safe key: "::" cannot appear in a uuid, so distinct id pairs
 * never collapse onto the same key regardless of digit boundaries. */
function pairKey(mfnClauseId: string, triggeringProvisionId: string): string {
  return `${mfnClauseId}::${triggeringProvisionId}`;
}

export function diffTriggers(
  computed: ComputedTriggerPair[],
  existing: ExistingTriggerRow[],
): MfnDiffResult {
  const computedKeys = new Set(
    computed.map((c) => pairKey(c.mfnClauseId, c.triggeringProvisionId)),
  );

  const newlyStaleIds: string[] = [];
  const revivedIds: string[] = [];

  for (const trigger of existing) {
    // Resolved/dismissed triggers are a permanent human decision — never touched.
    if (RESOLVED_STATUSES.has(trigger.status)) continue;

    const stillComputed = computedKeys.has(
      pairKey(trigger.mfn_clause_id, trigger.triggering_provision_id),
    );

    if (!trigger.stale && LIVE_STATUSES.has(trigger.status) && !stillComputed) {
      newlyStaleIds.push(trigger.id);
    } else if (trigger.stale && stillComputed) {
      revivedIds.push(trigger.id);
    }
  }

  return { newlyStaleIds, revivedIds };
}
